import React, { useState, useEffect } from 'react';
import Header from '../../layouts/Header';
import {
    Col,
    Row,
    Card,
    Table,
    Button,
    Form,
    Modal,
    Spinner,
    Badge,
    InputGroup,
    FormControl,
    Pagination
} from 'react-bootstrap';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import axios from '../../api/axios';
import { toast } from 'react-toastify';
import {
    Bell,
    Calendar,
    Clock,
    Store,
    Filter,
    Search,
    Plus,
    Edit,
    Trash2,
    RefreshCw,
    Tag,
    Users,
    Package,
    DollarSign,
    Image as ImageIcon,
    X,
    IndianRupee
} from 'lucide-react';
import { format } from 'date-fns';
import { EyeFill } from 'react-bootstrap-icons';

const NotificationScheduler = () => {
    const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
    const [skin, setSkin] = useState(currentSkin);

    const { access } = useSelector((state) => state.login);
    const [notificationAccess, setNotificationAccess] = useState([]);

    // Main states
    const [loading, setLoading] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [totalNotifications, setTotalNotifications] = useState(0);

    // Filter states
    const [searchQuery, setSearchQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState('ALL');
    const [statusFilter, setStatusFilter] = useState('ALL');

    // Modal states
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedNotification, setSelectedNotification] = useState(null);
    const [viewSingleData, setViewSingleData] = useState({});

    // Form states - REMOVED 'type' field as backend handles PUSH/EMAIL
    const [formData, setFormData] = useState({
        title: '',
        subTitle: '',
        startDate: '',
        endDate: '',
        time: '',
        outlet: null,
        // type field removed as backend handles it
        fromDate: '',
        toDate: '',
        minPrice: '',
        maxPrice: '',
        foodType: '',
        userType: '', // Default to ALL users
        category: null,
        product: null,
        type: 'PUSH', // Default type set to PUSH 
        image: null
    });
    const [imagePreview, setImagePreview] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Options states
    const [outlets, setOutlets] = useState([]);
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(25);
    const [totalPages, setTotalPages] = useState('');

    const accessToken = localStorage.getItem('authToken');

    // Get permissions
    useEffect(() => {
        if (Array.isArray(access)) {
            const filteredAccess = access?.filter((perm) =>
                perm?.startsWith("NotificationScheduler:")
            );
            setNotificationAccess(filteredAccess);
        }
    }, [access]);

    // Custom styles for react-select
    const customSelectStyles = {
        control: (base, state) => ({
            ...base,
            border: '1px solid #dee2e6',
            borderRadius: '0.375rem',
            minHeight: '38px',
            backgroundColor: skin === 'dark' ? '#2a3042' : 'white',
            color: skin === 'dark' ? 'white' : 'black',
            '&:hover': {
                borderColor: '#86b7fe',
            },
        }),
        menu: (base) => ({
            ...base,
            backgroundColor: skin === 'dark' ? '#2a3042' : 'white',
            color: skin === 'dark' ? 'white' : 'black',
            zIndex: 9999,
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isFocused
                ? (skin === 'dark' ? '#3a4155' : '#e9ecef')
                : (skin === 'dark' ? '#2a3042' : 'white'),
            color: skin === 'dark' ? 'white' : 'black',
            '&:hover': {
                backgroundColor: skin === 'dark' ? '#3a4155' : '#e9ecef',
            }
        }),
        singleValue: (base) => ({
            ...base,
            color: skin === 'dark' ? 'white' : 'black',
        }),
        placeholder: (base) => ({
            ...base,
            color: '#6c757d',
        }),
        input: (base) => ({
            ...base,
            color: skin === 'dark' ? 'white' : 'black',
        }),
    };

    // Fetch all data
    const fetchInitialData = async () => {
        await Promise.all([
            getNotifications(currentPage, itemsPerPage),
            getOutlets(),
            getCategories(),
            getProducts()
        ]);
    };

    // Fetch notifications
    const getNotifications = async (page, limit) => {
        setLoading(true);
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axios.get(
                `notification-scheduler?page=${page}&limit=${limit}`,
                { headers }
            );
            console.log("Response of Notifications :", response);

            if (response?.data?.status === true) {
                const data = response?.data?.data || {};
                setNotifications(data || []);
                setTotalNotifications(data.total || 0);
                setTotalPages(response?.data?.pagination?.totalPages);
            }
        } catch (error) {
            showError(error, 'Failed to fetch notifications');
        } finally {
            setLoading(false);
        }
    };

    // Fetch outlets
    const getOutlets = async () => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axios.get("outlet", { headers });

            if (response?.data?.status === true) {
                console.log("Response of Outlets :", response);

                const outletsData = response?.data?.data?.outlets || [];
                console.log("Outlets Data :", outletsData);

                const options = outletsData?.map(outlet => ({
                    value: outlet._id,
                    label: outlet.name || outlet.outletName
                }));
                console.log("Options Data :", options);
                setOutlets(options);
            }
        } catch (error) {
            console.error('Error fetching outlets:', error);
        }
    };

    // Fetch categories
    const getCategories = async () => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axios.get("masters/category/get", { headers });

            if (response?.data?.status === true) {
                const categoriesData = response?.data?.data?.categories || [];
                const options = categoriesData.map(category => ({
                    value: category._id,
                    label: category.name || category.categoryName
                }));
                setCategories(options);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    // Fetch products with search
    const getProducts = async (searchTerm = '') => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axios.get(`product?search=${searchTerm}`, { headers });

            if (response?.data?.status === true) {
                const productsData = response?.data?.data?.products || [];
                const options = productsData.map(product => ({
                    value: product._id,
                    label: product.name,
                    foodType: product.foodType
                }));
                return options;
            }
        } catch (error) {
            console.error('Error fetching products:', error);
        }
        return [];
    };

    // Load products for async select
    const loadProducts = async (inputValue) => {
        return await getProducts(inputValue);
    };

    // Handle form input change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle select change
    const handleSelectChange = (name, selectedOption) => {
        setFormData(prev => ({
            ...prev,
            [name]: selectedOption
        }));
    };

    // Handle image upload
    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Check file type
        const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'];
        if (!validTypes.includes(file.type)) {
            toast.error('Please upload a valid image file (JPEG, PNG, JPG, GIF)', {
                position: "top-right",
                autoClose: 3000,
            });
            return;
        }

        // Check file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Image size should be less than 5MB', {
                position: "top-right",
                autoClose: 3000,
            });
            return;
        }

        setFormData(prev => ({
            ...prev,
            image: file
        }));

        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
    };

    // Remove image
    const handleRemoveImage = () => {
        setFormData(prev => ({
            ...prev,
            image: null
        }));
        setImagePreview(null);
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            title: '',
            subTitle: '',
            startDate: '',
            endDate: '',
            time: '',
            outlet: null,
            // type field removed
            fromDate: '',
            toDate: '',
            minPrice: '',
            maxPrice: '',
            type: 'PUSH',
            foodType: '',
            userType: '',
            category: null,
            product: null,
            image: null
        });
        setImagePreview(null);
    };

    // Open add modal
    const handleAddNotification = () => {
        resetForm();
        setShowAddModal(true);
    };

    // Open edit modal
    // const handleEditNotification = (notification) => {
    //     setSelectedNotification(notification);

    //     // Convert notification data to form format - REMOVED type field
    //     const formattedData = {
    //         title: notification.title || '',
    //         subTitle: notification.subTitle || '',
    //         startDate: notification.startDate ? format(new Date(notification.startDate), 'yyyy-MM-dd') : '',
    //         endDate: notification.endDate ? format(new Date(notification.endDate), 'yyyy-MM-dd') : '',
    //         time: notification.time || '',
    //         outlet: notification.outlet ? {
    //             value: notification.outlet._id,
    //             label: notification.outlet.name
    //         } : null,
    //         // type field removed as backend handles it
    //         fromDate: notification.fromDate ? format(new Date(notification.fromDate), 'yyyy-MM-dd') : '',
    //         toDate: notification.toDate ? format(new Date(notification.toDate), 'yyyy-MM-dd') : '',
    //         minPrice: notification.minPrice || '',
    //         maxPrice: notification.maxPrice || '',
    //         foodType: notification.foodType || 'ALL',
    //         userType: notification.userType || 'ALL',
    //         category: notification.category ? {
    //             value: notification.category._id,
    //             label: notification.category.name
    //         } : null,
    //         product: notification.product ? {
    //             value: notification.product._id,
    //             label: notification.product.name
    //         } : null,
    //         image: null
    //     };

    //     setFormData(formattedData);

    //     // Set image preview if exists
    //     if (notification.image) {
    //         setImagePreview(`${axios.defaults.baseURL}${notification.image}`);
    //     }

    //     setShowEditModal(true);
    // };

    // Open View Modal
    const handleViewNotification = (notification) => {
        console.log("View Modal Data :", notification);

        setViewSingleData(notification);
        setShowViewModal(true);
    }

    // Open delete modal
    const handleDeleteNotification = (notification) => {
        setSelectedNotification(notification);
        setShowDeleteModal(true);
    };

    // Submit form (create or update) - UPDATED to not send 'type' field
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'multipart/form-data'
            };

            const formDataToSend = new FormData();

            // Add all form fields EXCEPT 'type' (handled by backend)
            Object.keys(formData).forEach(key => {
                if (key === 'outlet' && formData.outlet) {
                    formDataToSend.append(key, formData.outlet.value);
                } else if (key === 'category' && formData.category) {
                    formDataToSend.append(key, formData.category.value);
                } else if (key === 'product' && formData.product) {
                    formDataToSend.append(key, formData.product.value);
                } else if (key === 'image' && formData.image) {
                    formDataToSend.append(key, formData.image);
                } else if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
                    // 1Don't add 'type' field as backend handles PUSH/EMAIL
                    if (key !== 'type') {
                        formDataToSend.append(key, formData[key]);
                    }
                }
            });

            // FORCE APPEND TYPE (always send)
            formDataToSend.append('type', formData.type || 'PUSH');

            for (let pair of formDataToSend.entries()) {
                console.log(pair[0], pair[1]);
            }


            let response;
            if (showEditModal && selectedNotification) {
                // Update existing notification
                formDataToSend.append('id', selectedNotification._id);
                response = await axios.put('notification-scheduler', formDataToSend, { headers });
            } else {
                // Create new notification
                response = await axios.post('notification-scheduler', formDataToSend, { headers });
            }

            if (response?.data?.status === true) {
                toast.success(response?.data?.message || 'Notification saved successfully!', {
                    position: "top-right",
                    autoClose: 3000,
                });

                // Close modal and refresh data
                setShowAddModal(false);
                setShowEditModal(false);
                resetForm();
                getNotifications(currentPage, itemsPerPage);
            }
        } catch (error) {
            showError(error, 'Failed to save notification');
        } finally {
            setSubmitting(false);
        }
    };

    // Delete notification
    const handleDelete = async () => {
        setDeleteLoading(true);

        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            };

            const data = {
                id: selectedNotification._id
            };

            const response = await axios.delete('notification-scheduler', { data, headers });

            if (response?.data?.status === true) {
                toast.success('Notification deleted successfully!', {
                    position: "top-right",
                    autoClose: 3000,
                });
                setShowDeleteModal(false);
                getNotifications(currentPage, itemsPerPage);
            }
        } catch (error) {
            showError(error, 'Failed to delete notification');
        } finally {
            setDeleteLoading(false);
        }
    };

    // Error handler
    const showError = (error, defaultMessage) => {
        const errorMessage = error.response?.data?.error || error.response?.data?.message || defaultMessage;
        toast.error(errorMessage, {
            position: "top-right",
            autoClose: 3000,
        });
    };

    // Format date
    const formatDate = (dateString) => {
        try {
            return format(new Date(dateString), 'dd/MM/yyyy');
        } catch {
            return dateString;
        }
    };

    // Format datetime
    const formatDateTime = (dateString) => {
        try {
            return format(new Date(dateString), 'dd/MM/yyyy HH:mm');
        } catch {
            return dateString;
        }
    };

    // Get status badge
    const getStatusBadge = (startDate, endDate) => {
        const now = new Date();
        const start = new Date(startDate);
        const end = new Date(endDate);

        if (now < start) {
            return <Badge bg="warning">Upcoming</Badge>;
        } else if (now >= start && now <= end) {
            return <Badge bg="success">Active</Badge>;
        } else {
            return <Badge bg="secondary">Expired</Badge>;
        }
    };

    // Get type badge - Updated for backend handled types
    const getTypeBadge = (type) => {
        const variants = {
            PUSH: 'primary',
            EMAIL: 'info'
        };
        return <Badge bg={variants[type] || 'secondary'}>{type}</Badge>;
    };

    // Pagination
    const paginationItems = [];

    for (let number = 1; number <= totalPages; number++) {
        paginationItems?.push(
            <Pagination.Item
                key={number}
                active={number === currentPage}
                onClick={() => getNotifications(number, itemsPerPage)}
            >
                {number}
            </Pagination.Item>
        );
    }

    // Initialize
    useEffect(() => {
        fetchInitialData();
    }, []);

    return (
        <>
            <Header onSkin={setSkin} />

            <div className="main main-app p-3 p-lg-4">
                <div className="container shadow-sm" style={{ backgroundColor: "white" }}>
                    {/* Header */}
                    <Row className="pt-3">
                        <Col>
                            <div className="d-flex align-items-center justify-content-between mb-1">
                                <div className="d-flex align-items-center">
                                    <Bell className="me-3" size={26} color="#0d6efd" />
                                    <h4 className="fw-semibold mb-0">NOTIFICATION SCHEDULER</h4>
                                </div>
                                <Button
                                    variant="primary"
                                    onClick={handleAddNotification}
                                // disabled={!notificationAccess?.includes("NotificationScheduler:add")}
                                >
                                    <Plus size={16} className="me-2" />
                                    Schedule Notification
                                </Button>
                            </div>
                        </Col>
                        <hr />
                    </Row>

                    {/* Filters Section */}
                    <Row className="mb-4">
                        <Col md={6}>
                            <InputGroup>
                                <InputGroup.Text>
                                    <Search size={18} />
                                </InputGroup.Text>
                                <FormControl
                                    placeholder="Search notifications..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </InputGroup>
                        </Col>
                        <Col md={3}>
                            <Form.Select
                                value={typeFilter}
                                onChange={(e) => setTypeFilter(e.target.value)}
                            >
                                <option value="ALL">All Types</option>
                                <option value="PUSH">Push Notifications</option>
                                <option value="EMAIL">Email Notifications</option>
                            </Form.Select>
                        </Col>
                        <Col md={3}>
                            <Form.Select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <option value="ALL">All Status</option>
                                <option value="ACTIVE">Active</option>
                                <option value="UPCOMING">Upcoming</option>
                                <option value="EXPIRED">Expired</option>
                            </Form.Select>
                        </Col>
                    </Row>

                    {/* Notifications Count */}
                    <Row className="mb-3">
                        <Col>
                            <div className="d-flex justify-content-between align-items-center">
                                <h6 className="mb-0">
                                    Total Notifications: <strong>{totalNotifications}</strong>
                                </h6>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => getNotifications(currentPage, itemsPerPage)}
                                    disabled={loading}
                                >
                                    <RefreshCw size={16} className="me-2" />
                                    {loading ? 'Refreshing...' : 'Refresh'}
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    {/* Notifications Table */}
                    <Row>
                        <Col>
                            <Card className='mb-3'>
                                <Card.Header>
                                    <h6 className="mb-0">Scheduled Notifications</h6>
                                </Card.Header>
                                <Card.Body>
                                    {loading ? (
                                        <div className="text-center py-4">
                                            <Spinner animation="border" variant="primary" />
                                            <p className="mt-2 mb-0">Loading notifications...</p>
                                        </div>
                                    ) : notifications?.length > 0 ? (
                                        <>
                                            <div className="table-responsive">
                                                <Table striped bordered hover>
                                                    <thead>
                                                        <tr>
                                                            <th>Title</th>
                                                            <th>Type</th>
                                                            <th>Schedule</th>
                                                            <th>Status</th>
                                                            <th>Created</th>
                                                            <th className="text-center">Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {notifications?.map((notification) => (
                                                            <tr key={notification._id}>
                                                                <td>
                                                                    <div className="fw-semibold">{notification.title}</div>
                                                                    <small className="text-muted">{notification.subTitle}</small>
                                                                </td>
                                                                <td>
                                                                    {getTypeBadge(notification.type)}
                                                                    {notification.outlet && (
                                                                        <div className="mt-1">
                                                                            <small className="text-muted">
                                                                                <Store size={12} className="me-1" />
                                                                                {notification.outlet.name}
                                                                            </small>
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <div>
                                                                        <small className="text-muted d-block">
                                                                            <Calendar size={12} className="me-1" />
                                                                            {formatDate(notification.startDate)} - {formatDate(notification.endDate)}
                                                                        </small>
                                                                        <small className="text-muted">
                                                                            <Clock size={12} className="me-1" />
                                                                            {notification.time}
                                                                        </small>
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    {getStatusBadge(notification.startDate, notification.endDate)}
                                                                </td>
                                                                <td>
                                                                    <small className="text-muted">
                                                                        {formatDateTime(notification.createdAt)}
                                                                    </small>
                                                                </td>
                                                                <td className="text-center">
                                                                    <div className="d-flex gap-2 justify-content-center">
                                                                        {/* <Button
                                                                            variant="outline-primary"
                                                                            size="sm"
                                                                            onClick={() => handleEditNotification(notification)}
                                                                            disabled={!notificationAccess?.includes("NotificationScheduler:edit")}
                                                                            title="Edit"
                                                                        >
                                                                            <Edit size={14} />
                                                                        </Button> */}
                                                                        <Button
                                                                            variant="outline-primary"
                                                                            size="sm"
                                                                            onClick={() => handleViewNotification(notification)}
                                                                            // disabled={!notificationAccess?.includes("NotificationScheduler:edit")}
                                                                            title="Edit"
                                                                        >
                                                                            <EyeFill size={14} />
                                                                        </Button>
                                                                        <Button
                                                                            variant="outline-danger"
                                                                            size="sm"
                                                                            onClick={() => handleDeleteNotification(notification)}
                                                                            // disabled=    {!notificationAccess?.includes("NotificationScheduler:delete")}
                                                                            title="Delete"
                                                                        >
                                                                            <Trash2 size={14} />
                                                                        </Button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>

                                            {/* Pagination */}
                                            {totalPages > 1 && (
                                                <div className="d-flex justify-content-center mt-4">
                                                    <Pagination>
                                                        <Pagination.Prev
                                                            onClick={() => currentPage > 1 && getNotifications(currentPage - 1, itemsPerPage)}
                                                            disabled={currentPage === 1}
                                                        />
                                                        {paginationItems}
                                                        <Pagination.Next
                                                            onClick={() => currentPage < totalPages && getNotifications(currentPage + 1, itemsPerPage)}
                                                            disabled={currentPage === totalPages}
                                                        />
                                                    </Pagination>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div className="text-center py-4">
                                            <Bell size={48} className="text-muted mb-3" />
                                            <h5>No Notifications Found</h5>
                                            <p className="text-muted mb-4">
                                                No scheduled notifications found. Schedule your first notification!
                                            </p>
                                            <Button
                                                variant="primary"
                                                onClick={handleAddNotification}
                                                disabled={!notificationAccess?.includes("NotificationScheduler:add")}
                                            >
                                                <Plus size={16} className="me-2" />
                                                Schedule Notification
                                            </Button>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>

                {/* Add/Edit Modal */}
                <Modal show={showAddModal || showEditModal} onHide={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                    resetForm();
                }} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <Bell size={20} className="me-2" />
                            {showEditModal ? 'Edit Notification' : 'Schedule New Notification'}
                        </Modal.Title>
                    </Modal.Header>
                    <Form onSubmit={handleSubmit}>
                        <Modal.Body>
                            <Row>
                                {/* Basic Information */}
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold">Title <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="title"
                                            value={formData.title}
                                            onChange={handleInputChange}
                                            placeholder="Enter notification title"
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold">Subtitle</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="subTitle"
                                            value={formData.subTitle}
                                            onChange={handleInputChange}
                                            placeholder="Enter notification subtitle"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            {/* Schedule Information */}
                            <Row>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold">Start Date <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="startDate"
                                            value={formData.startDate}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold">End Date <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="endDate"
                                            value={formData.endDate}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold">Time <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="time"
                                            name="time"
                                            value={formData.time}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            {/* User Filters Section */}
                            <Row className="mb-3">
                                <Col>
                                    <div className="border rounded p-3 mb-3">
                                        <h6 className="fw-semibold mb-3">User Filters</h6>

                                        {/* User Type */}
                                        <Row className="mb-3">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">User Type <span className="text-danger">*</span></Form.Label>
                                                    <Form.Select
                                                        name="userType"
                                                        value={formData.userType}
                                                        onChange={handleInputChange}
                                                        required
                                                    >
                                                        <option value="ALL">All Users</option>
                                                        <option value="JOINING_COUPON_ONE_ORDER">Joining Coupon (One Order)</option>
                                                        <option value="COUPON_ORDERS">Coupon Orders</option>
                                                        <option value="NO_COUPON_ORDERS">No Coupon Orders</option>
                                                        <option value="REFERRING_USERS">Referring Users</option>
                                                        <option value="REFERRED_USERS">Referred Users</option>
                                                        <option value="REPEATED_USERS_DAYS">Repeated Users (Days)</option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Outlet</Form.Label>
                                                    <Select
                                                        options={outlets}
                                                        value={formData.outlet}
                                                        onChange={(selected) => handleSelectChange('outlet', selected)}
                                                        placeholder="Select outlet..."
                                                        styles={customSelectStyles}
                                                        isClearable
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        {/* Date Range Filter */}
                                        <Row className="mb-3">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">From Date</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        name="fromDate"
                                                        value={formData.fromDate}
                                                        onChange={handleInputChange}
                                                        placeholder="Filter users from date"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">To Date</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        name="toDate"
                                                        value={formData.toDate}
                                                        onChange={handleInputChange}
                                                        placeholder="Filter users to date"
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        {/* Additional Filters */}
                                        <Row className="mb-3">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Food Type</Form.Label>
                                                    <Form.Select
                                                        name="foodType"
                                                        value={formData.foodType}
                                                        onChange={handleInputChange}
                                                    >
                                                        <option value="ALL">All Types</option>
                                                        <option value="VEG">Vegetarian</option>
                                                        <option value="NON_VEG">Non-Vegetarian</option>
                                                        <option value="VEGAN">Vegan</option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Price Range</Form.Label>
                                                    <div className="d-flex gap-2">
                                                        <Form.Control
                                                            type="number"
                                                            name="minPrice"
                                                            value={formData.minPrice}
                                                            onChange={handleInputChange}
                                                            placeholder="Min Price (₹)"
                                                            min="0"
                                                        />
                                                        <Form.Control
                                                            type="number"
                                                            name="maxPrice"
                                                            value={formData.maxPrice}
                                                            onChange={handleInputChange}
                                                            placeholder="Max Price (₹)"
                                                            min="0"
                                                        />
                                                    </div>
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        {/* Category and Product Filters */}
                                        <Row className="mb-3">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Category</Form.Label>
                                                    <Select
                                                        options={categories}
                                                        value={formData.category}
                                                        onChange={(selected) => handleSelectChange('category', selected)}
                                                        placeholder="Select category..."
                                                        styles={customSelectStyles}
                                                        isClearable
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Product</Form.Label>
                                                    <AsyncSelect
                                                        cacheOptions
                                                        defaultOptions
                                                        loadOptions={loadProducts}
                                                        value={formData.product}
                                                        onChange={(selected) => handleSelectChange('product', selected)}
                                                        placeholder="Search and select product..."
                                                        styles={customSelectStyles}
                                                        isClearable
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        {/* Outlet Filter */}
                                        {/* <Row>
                                            <Col>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Outlet</Form.Label>
                                                    <Select
                                                        options={outlets}
                                                        value={formData.outlet}
                                                        onChange={(selected) => handleSelectChange('outlet', selected)}
                                                        placeholder="Select outlet..."
                                                        styles={customSelectStyles}
                                                        isClearable
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row> */}
                                    </div>
                                </Col>
                            </Row>

                            {/* Image Upload */}
                            <Row className="mb-3">
                                <Col>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold">Notification Image</Form.Label>
                                        {imagePreview ? (
                                            <div className="position-relative">
                                                <img
                                                    src={imagePreview}
                                                    alt="Preview"
                                                    className="img-fluid rounded mb-2"
                                                    style={{ maxHeight: '200px' }}
                                                />
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    className="position-absolute top-0 end-0 m-2"
                                                    onClick={handleRemoveImage}
                                                >
                                                    <X size={14} />
                                                </Button>
                                            </div>
                                        ) : (
                                            <div className="border rounded p-4 text-center">
                                                <ImageIcon size={48} className="text-muted mb-2" />
                                                <p className="text-muted mb-2">Upload notification image (Max 5MB)</p>
                                                <Form.Control
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={handleImageUpload}
                                                />
                                            </div>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setShowAddModal(false);
                                    setShowEditModal(false);
                                    resetForm();
                                }}
                                disabled={submitting}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={submitting}
                            >
                                {submitting ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-2"
                                        />
                                        {showEditModal ? 'Updating...' : 'Scheduling...'}
                                    </>
                                ) : (
                                    showEditModal ? 'Update Notification' : 'Schedule Notification'
                                )}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </Modal>

                {/* Delete Confirmation Modal */}
                <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm Delete</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>Are you sure you want to delete this notification?</p>
                        <div className="alert alert-warning">
                            <strong>Title:</strong> {selectedNotification?.title}<br />
                            <strong>Type:</strong> {selectedNotification?.type}<br />
                            <strong>Schedule:</strong> {selectedNotification?.startDate ? formatDate(selectedNotification.startDate) : 'N/A'} - {selectedNotification?.endDate ? formatDate(selectedNotification.endDate) : 'N/A'}
                        </div>
                        <p className="text-muted small">
                            This action cannot be undone. The notification will be permanently deleted.
                        </p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="secondary"
                            onClick={() => setShowDeleteModal(false)}
                            disabled={deleteLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={handleDelete}
                            disabled={deleteLoading}
                        >
                            {deleteLoading ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                        className="me-2"
                                    />
                                    Deleting...
                                </>
                            ) : (
                                "Delete Notification"
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* View Details Modal */}
                {showViewModal && (
                    <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg">
                        <Modal.Header closeButton>
                            <Modal.Title>
                                <Bell size={20} className="me-2" />
                                Notification Details
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {viewSingleData ? (
                                <div className="notification-details">
                                    {/* Header with Title and Status */}
                                    <div className="d-flex align-items-center justify-content-between mb-4">
                                        <div>
                                            <h4 className="fw-bold mb-1">{viewSingleData.title}</h4>
                                            <p className="text-muted mb-0">{viewSingleData.subTitle}</p>
                                        </div>
                                        <div>
                                            {getTypeBadge(viewSingleData.type)} <div></div>
                                            {getStatusBadge(viewSingleData.startDate, viewSingleData.endDate)}
                                        </div>
                                    </div>

                                    {/* Image Preview */}
                                    {viewSingleData.image && (
                                        <div className="text-center mb-4">
                                            <img
                                                src={`${axios.defaults.baseURL}${viewSingleData.image}`}
                                                alt="Notification"
                                                className="img-fluid rounded"
                                                style={{ maxHeight: '250px', objectFit: 'contain' }}
                                            />
                                        </div>
                                    )}

                                    {/* Details in Cards */}
                                    <Row>
                                        {/* Schedule Information */}
                                        <Col md={6}>
                                            <Card className="mb-3">
                                                <Card.Header className="bg-light">
                                                    <h6 className="mb-0">
                                                        <Calendar size={16} className="me-2" />
                                                        Schedule Information
                                                    </h6>
                                                </Card.Header>
                                                <Card.Body>
                                                    <div className="mb-2">
                                                        <small className="text-muted d-block">Start Date:</small>
                                                        <strong>{formatDate(viewSingleData.startDate)}</strong>
                                                    </div>
                                                    <div className="mb-2">
                                                        <small className="text-muted d-block">End Date:</small>
                                                        <strong>{formatDate(viewSingleData.endDate)}</strong>
                                                    </div>
                                                    <div className="mb-2">
                                                        <small className="text-muted d-block">Time:</small>
                                                        <strong>{viewSingleData.time}</strong>
                                                    </div>
                                                    <div className="mb-2">
                                                        <small className="text-muted d-block">Type:</small>
                                                        <strong>{viewSingleData.type}</strong>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>

                                        {/* User Filters */}
                                        <Col md={6}>
                                            <Card className="mb-3">
                                                <Card.Header className="bg-light">
                                                    <h6 className="mb-0">
                                                        <Users size={16} className="me-2" />
                                                        User Filters
                                                    </h6>
                                                </Card.Header>
                                                <Card.Body>
                                                    {viewSingleData.userType ? (
                                                        <div className="mb-2">
                                                            <small className="text-muted d-block">User Type:</small>
                                                            <strong>
                                                                {viewSingleData.userType === 'JOINING_COUPON_ONE_ORDER' ? 'Joining Coupon (One Order)' :
                                                                    viewSingleData.userType === 'COUPON_ORDERS' ? 'Coupon Orders' :
                                                                        viewSingleData.userType === 'NO_COUPON_ORDERS' ? 'No Coupon Orders' :
                                                                            viewSingleData.userType === 'REFERRING_USERS' ? 'Referring Users' :
                                                                                viewSingleData.userType === 'REFERRED_USERS' ? 'Referred Users' :
                                                                                    viewSingleData.userType === 'REPEATED_USERS_DAYS' ? 'Repeated Users (Days)' :
                                                                                        viewSingleData.userType}
                                                            </strong>
                                                        </div>
                                                    ) : (
                                                        <div className="mb-2">
                                                            <small className="text-muted d-block">User Type:</small>
                                                            <strong>All Users</strong>
                                                        </div>
                                                    )}

                                                    {/* Date Range */}
                                                    {(viewSingleData.fromDate || viewSingleData.toDate) && (
                                                        <>
                                                            <div className="mb-2">
                                                                <small className="text-muted d-block">Date Range:</small>
                                                                <div className="d-flex gap-2">
                                                                    {viewSingleData.fromDate && (
                                                                        <span className="badge bg-light text-dark">
                                                                            From: {formatDate(viewSingleData.fromDate)}
                                                                        </span>
                                                                    )}
                                                                    {viewSingleData.toDate && (
                                                                        <span className="badge bg-light text-dark">
                                                                            To: {formatDate(viewSingleData.toDate)}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </>
                                                    )}
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>

                                    {/* Additional Filters */}
                                    <Row>
                                        {/* Price Range */}
                                        {(viewSingleData.minPrice || viewSingleData.maxPrice) && (
                                            <Col md={6}>
                                                <Card className="mb-3">
                                                    <Card.Header className="bg-light">
                                                        <h6 className="mb-0">
                                                            <IndianRupee size={16} className="me-2" />
                                                            Price Range
                                                        </h6>
                                                    </Card.Header>
                                                    <Card.Body>
                                                        <div className="d-flex justify-content-between">
                                                            {viewSingleData.minPrice && (
                                                                <div>
                                                                    <small className="text-muted d-block">Min Price:</small>
                                                                    <strong>₹{viewSingleData.minPrice}</strong>
                                                                </div>
                                                            )}
                                                            {viewSingleData.maxPrice && (
                                                                <div>
                                                                    <small className="text-muted d-block">Max Price:</small>
                                                                    <strong>₹{viewSingleData.maxPrice}</strong>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        )}

                                        {/* Food Type */}
                                        {viewSingleData.foodType && viewSingleData.foodType !== 'ALL' && (
                                            <Col md={6}>
                                                <Card className="mb-3">
                                                    <Card.Header className="bg-light">
                                                        <h6 className="mb-0">
                                                            <Package size={16} className="me-2" />
                                                            Food Type
                                                        </h6>
                                                    </Card.Header>
                                                    <Card.Body>
                                                        <div className="mb-2">
                                                            <small className="text-muted d-block">Filter:</small>
                                                            <strong>
                                                                {viewSingleData.foodType === 'VEG' ? 'Vegetarian' :
                                                                    viewSingleData.foodType === 'NON_VEG' ? 'Non-Vegetarian' :
                                                                        viewSingleData.foodType === 'VEGAN' ? 'Vegan' :
                                                                            viewSingleData.foodType}
                                                            </strong>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        )}
                                    </Row>

                                    {/* Target Specifics */}
                                    <Row>
                                        {/* Outlet */}
                                        {viewSingleData.outlet && (
                                            <Col md={6}>
                                                <Card className="mb-3">
                                                    <Card.Header className="bg-light">
                                                        <h6 className="mb-0">
                                                            <Store size={16} className="me-2" />
                                                            Outlet
                                                        </h6>
                                                    </Card.Header>
                                                    <Card.Body>
                                                        <div className="mb-2">
                                                            <small className="text-muted d-block">Outlet Name:</small>
                                                            <strong>{viewSingleData.outlet.name}</strong>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        )}

                                        {/* Category */}
                                        {viewSingleData.category && (
                                            <Col md={6}>
                                                <Card className="mb-3">
                                                    <Card.Header className="bg-light">
                                                        <h6 className="mb-0">
                                                            <Tag size={16} className="me-2" />
                                                            Category
                                                        </h6>
                                                    </Card.Header>
                                                    <Card.Body>
                                                        <div className="mb-2">
                                                            <small className="text-muted d-block">Category Name:</small>
                                                            <strong>{viewSingleData.category.name}</strong>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        )}

                                        {/* Product */}
                                        {viewSingleData.product && (
                                            <Col md={6}>
                                                <Card className="mb-3">
                                                    <Card.Header className="bg-light">
                                                        <h6 className="mb-0">
                                                            <Package size={16} className="me-2" />
                                                            Product
                                                        </h6>
                                                    </Card.Header>
                                                    <Card.Body>
                                                        <div className="mb-2">
                                                            <small className="text-muted d-block">Product Name:</small>
                                                            <strong>{viewSingleData.product.name}</strong>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        )}
                                    </Row>

                                    {/* Meta Information */}
                                    {/* <Card className="mt-3">
                                        <Card.Header className="bg-light">
                                            <h6 className="mb-0">
                                                <Clock size={16} className="me-2" />
                                                Meta Information
                                            </h6>
                                        </Card.Header>
                                        <Card.Body>
                                            <Row>
                                                <Col md={6}>
                                                    <div className="mb-2">
                                                        <small className="text-muted d-block">Created At:</small>
                                                        <strong>{formatDateTime(viewSingleData.createdAt)}</strong>
                                                    </div>
                                                </Col>
                                                <Col md={6}>
                                                    <div className="mb-2">
                                                        <small className="text-muted d-block">Last Updated:</small>
                                                        <strong>{formatDateTime(viewSingleData.updatedAt)}</strong>
                                                    </div>
                                                </Col>
                                                <Col md={12}>
                                                    <div className="mb-2">
                                                        <small className="text-muted d-block">Notification ID:</small>
                                                        <code className="text-muted">{viewSingleData._id}</code>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                    </Card> */}

                                    {/* Status Summary */}
                                    <div className="mt-4 p-3 rounded bg-light">
                                        <h6 className="mb-2">Current Status:</h6>
                                        <div className="d-flex align-items-center gap-3">
                                            <div>
                                                <small className="text-muted d-block">Schedule Status:</small>
                                                {getStatusBadge(viewSingleData.startDate, viewSingleData.endDate)}
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">Notification Type:</small>
                                                {getTypeBadge(viewSingleData.type)}
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">Target Users:</small>
                                                <Badge bg="info" className="ms-1">
                                                    {viewSingleData.userType ? 'Specific Users' : 'All Users'}
                                                </Badge>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-4">
                                    <Spinner animation="border" variant="primary" />
                                    <p className="mt-2 mb-0">Loading notification details...</p>
                                </div>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowViewModal(false)}>
                                Close
                            </Button>
                            {/* {viewSingleData && (
                                <Button
                                    variant="primary"
                                    onClick={() => {
                                        setShowViewModal(false);
                                        handleEditNotification(viewSingleData);
                                    }}
                                    disabled={!notificationAccess?.includes("NotificationScheduler:edit")}
                                >
                                    <Edit size={16} className="me-2" />
                                    Edit Notification
                                </Button>
                            )} */}
                        </Modal.Footer>
                    </Modal>
                )}
            </div>
        </>
    );
};

export default NotificationScheduler;